<template>
  <div class="leaderboard-page">
    <a-card class="leaderboard-toolbar" :body-style="{ padding: '12px 16px' }">
      <div class="leaderboard-toolbar-inner">
        <a-space size="small" wrap>
          <a-button type="primary" :loading="loadingGlobal" @click="loadAll">刷新</a-button>
          <span class="helper-text">展示前</span>
          <a-select
            v-model:value="limit"
            style="width: 110px"
            :options="limitOptions"
            @change="loadAll"
          />
          <span class="helper-text">条（每 5 秒自动刷新）</span>
        </a-space>
        <div class="leaderboard-share-controls">
          <label class="visibility-control">
            <a-switch
              v-model:checked="showNodeNames"
              data-testid="toggle-node-name"
              size="small"
            />
            <span>节点名称</span>
          </label>
          <label class="visibility-control">
            <a-switch
              v-model:checked="showProxyUsers"
              data-testid="toggle-proxy-user"
              size="small"
            />
            <span>所属用户</span>
          </label>
          <template v-if="posterMode">
            <span class="helper-text">海报前</span>
            <a-select
              v-model:value="posterLimit"
              class="poster-limit-select"
              :options="posterLimitOptions"
            />
            <span class="helper-text">名</span>
          </template>
          <a-button
            data-testid="toggle-poster-mode"
            :type="posterMode ? 'primary' : 'default'"
            @click="posterMode = !posterMode"
          >
            {{ posterMode ? '退出海报' : '海报模式' }}
          </a-button>
          <a-button
            v-if="posterMode"
            data-testid="download-poster"
            type="primary"
            ghost
            :loading="posterExporting"
            @click="downloadPoster"
          >
            分享海报
          </a-button>
        </div>
      </div>
    </a-card>

    <section
      ref="posterRef"
      data-testid="leaderboard-poster"
      class="leaderboard-poster"
      :class="{ 'leaderboard-poster--active': posterMode }"
    >
      <header v-if="posterMode" class="poster-hero">
        <div class="poster-hero-copy">
          <div class="poster-eyebrow">RXJH NETWORK PULSE</div>
          <h1>热血江湖 · 实时榜单</h1>
          <p>游戏节点连接表现</p>
        </div>
        <div class="poster-hero-meta">
          <span>{{ posterTimestamp }}</span>
          <span v-if="!showNodeNames">节点仅显示 ID</span>
          <span v-if="!showProxyUsers">用户信息已隐藏</span>
        </div>
      </header>

      <a-row :gutter="[16, 16]" class="leaderboard-grid">
      <a-col :xs="24" :lg="8">
        <a-card
          class="leaderboard-card leaderboard-card--duration"
          :title="posterMode ? '在线时长排行' : '时长排行榜（当前时长）'"
          :body-style="{ padding: 0 }"
        >
          <template #extra>
            <a-tag color="blue">实时展示</a-tag>
          </template>
          <a-list
            :data-source="visibleDurationRows"
            :loading="loadingDuration"
            :pagination="false"
            class="leaderboard-list-wrap"
          >
            <template #empty>
              <div class="leaderboard-empty">当前无活跃连接</div>
            </template>
            <template #renderItem="{ item }">
              <a-list-item class="leaderboard-list-item">
                <div class="leaderboard-item-head">
                  <a-tag :color="rankColor(item.rank)">#{{ item.rank }}</a-tag>
                  <div class="leaderboard-item-name">{{ item.proxy_id }}</div>
                </div>
                <div class="leaderboard-item-main">
                  <div class="meta-line">{{ formatNode(item) }}</div>
                  <div class="meta-line">{{ item.region_names.length ? item.region_names.join(' / ') : '未设置大区' }}</div>
                  <div class="meta-line">{{ item.game_server || '未识别' }}</div>
                  <div v-if="showProxyUsers" class="meta-line">所属用户：{{ formatProxyUser(item) }}</div>
                </div>
                <div class="leaderboard-item-metric">
                  <div class="metric-label">时长</div>
                  <div class="metric-value">{{ formatDuration(item.duration_ms) }}</div>
                </div>
              </a-list-item>
            </template>
          </a-list>
        </a-card>
      </a-col>

      <a-col :xs="24" :lg="8">
        <a-card
          class="leaderboard-card leaderboard-card--traffic"
          :title="posterMode ? '游戏连接流量排行' : '当前游戏连接流量排行榜'"
          :body-style="{ padding: 0 }"
        >
          <template #extra>
            <a-tag color="green">当前游戏连接</a-tag>
          </template>
          <a-list
            :data-source="visibleTrafficRows"
            :loading="loadingTraffic"
            :pagination="false"
            class="leaderboard-list-wrap"
          >
            <template #empty>
              <div class="leaderboard-empty">暂无流量排行数据</div>
            </template>
            <template #renderItem="{ item }">
              <a-list-item class="leaderboard-list-item">
                <div class="leaderboard-item-head">
                  <a-tag :color="rankColor(item.rank)">#{{ item.rank }}</a-tag>
                  <div class="leaderboard-item-name">{{ item.proxy_id }}</div>
                </div>
                <div class="leaderboard-item-main">
                  <div class="meta-line">{{ formatNode(item) }}</div>
                  <div class="meta-line">{{ item.region_names.length ? item.region_names.join(' / ') : '未设置大区' }}</div>
                  <div class="meta-line">{{ item.game_server || '未识别' }}</div>
                  <div v-if="showProxyUsers" class="meta-line">所属用户：{{ formatProxyUser(item) }}</div>
                </div>
                <div class="leaderboard-item-metric">
                  <div class="metric-label">连接流量</div>
                  <div class="metric-value">{{ formatBytes(item.bytes_total) }}</div>
                  <div class="metric-sub">当前连接 {{ item.active_connections || 0 }}</div>
                </div>
              </a-list-item>
            </template>
          </a-list>
        </a-card>
      </a-col>

      <a-col :xs="24" :lg="8">
        <a-card
          class="leaderboard-card leaderboard-card--speed"
          :title="posterMode ? '实时速度排行' : '实时速度排行榜（当前速率）'"
          :body-style="{ padding: 0 }"
        >
          <template #extra>
            <a-tag color="orange">bytes/s</a-tag>
          </template>
          <a-list
            :data-source="visibleSpeedRows"
            :loading="loadingSpeed"
            :pagination="false"
            class="leaderboard-list-wrap"
          >
            <template #empty>
              <div class="leaderboard-empty">暂无实时速率数据</div>
            </template>
            <template #renderItem="{ item }">
              <a-list-item class="leaderboard-list-item">
                <div class="leaderboard-item-head">
                  <a-tag :color="rankColor(item.rank)">#{{ item.rank }}</a-tag>
                  <div class="leaderboard-item-name">{{ item.proxy_id }}</div>
                </div>
                <div class="leaderboard-item-main">
                  <div class="meta-line">{{ formatNode(item) }}</div>
                  <div class="meta-line">{{ item.region_names.length ? item.region_names.join(' / ') : '未设置大区' }}</div>
                  <div class="meta-line">{{ item.game_server || '未识别' }}</div>
                  <div v-if="showProxyUsers" class="meta-line">所属用户：{{ formatProxyUser(item) }}</div>
                </div>
                <div class="leaderboard-item-metric">
                  <div class="metric-label">实时速率</div>
                  <div class="metric-value">{{ formatSpeed(item.speed_bps) }}</div>
                  <div class="metric-sub">当前连接 {{ item.active_connections || 0 }}</div>
                </div>
              </a-list-item>
            </template>
          </a-list>
        </a-card>
      </a-col>
      </a-row>

      <footer v-if="posterMode" class="poster-footer">
        <span>实时数据 · 每 5 秒更新</span>
        <span>热血江湖节点代理</span>
      </footer>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import { message } from 'ant-design-vue';
import {
  getConnectionLeaderboard,
  getSpeedLeaderboard,
  getTrafficLeaderboard,
} from '../api/proxies';
import type {
  ProxyConnectionLeaderboardItem,
  ProxySpeedLeaderboardItem,
  ProxyTrafficLeaderboardItem,
} from '../types';
import { formatTrafficBytes, formatTrafficRate } from '../utils/trafficFormat';

interface BaseRow {
  proxy_id: number;
  node_id: number;
  node_name: string;
  region_names: string[];
  game_server: string;
  proxy_user_id: number | null;
  proxy_user_wechat_nickname: string | null;
  proxy_user_wechat_id: string | null;
  active_connections: number;
  rank: number;
}

interface DurationRow extends BaseRow {
  duration_ms: number;
}

interface TrafficRow extends BaseRow {
  bytes_total: number;
}

interface SpeedRow extends BaseRow {
  speed_bps: number;
}

const durationRows = ref<DurationRow[]>([]);
const trafficRows = ref<TrafficRow[]>([]);
const speedRows = ref<SpeedRow[]>([]);

const loadingDuration = ref(false);
const loadingTraffic = ref(false);
const loadingSpeed = ref(false);
const loadingGlobal = computed(() => loadingDuration.value || loadingTraffic.value || loadingSpeed.value);
const showNodeNames = ref(true);
const showProxyUsers = ref(true);
const posterMode = ref(false);
const posterLimit = ref(3);
const posterExporting = ref(false);
const posterRef = ref<HTMLElement | null>(null);
const lastUpdatedAt = ref<Date | null>(null);

const limit = ref(50);
const limitOptions = [
  { value: 20, label: '20' },
  { value: 50, label: '50' },
  { value: 100, label: '100' },
  { value: 200, label: '200' },
];
const posterLimitOptions = [
  { value: 3, label: '3' },
  { value: 5, label: '5' },
  { value: 10, label: '10' },
];

const visibleDurationRows = computed(() =>
  posterMode.value ? durationRows.value.slice(0, posterLimit.value) : durationRows.value,
);
const visibleTrafficRows = computed(() =>
  posterMode.value ? trafficRows.value.slice(0, posterLimit.value) : trafficRows.value,
);
const visibleSpeedRows = computed(() =>
  posterMode.value ? speedRows.value.slice(0, posterLimit.value) : speedRows.value,
);
const posterTimestamp = computed(() => {
  const value = lastUpdatedAt.value || new Date();
  return value.toLocaleString('zh-CN', { hour12: false });
});

let refreshTimer: number | undefined;

const formatBytes = formatTrafficBytes;

const formatDuration = (ms: number): string => {
  const total = Number(ms) || 0;
  if (total <= 0) return '0秒';
  const totalSeconds = Math.floor(total / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}小时${minutes}分${seconds}秒`;
  if (minutes > 0) return `${minutes}分${seconds}秒`;
  return `${seconds}秒`;
};

const formatSpeed = (bps: number): string => {
  return formatTrafficRate(bps);
};

const rankColor = (rank: number): string => {
  if (rank === 1) return 'gold';
  if (rank === 2) return 'blue';
  if (rank === 3) return 'green';
  return 'default';
};

const formatProxyUser = (item: BaseRow): string => {
  const nickname = item.proxy_user_wechat_nickname?.trim() || '';
  const wechatID = item.proxy_user_wechat_id?.trim() || '';
  if (nickname && wechatID) return `${nickname} · ${wechatID}`;
  return nickname || wechatID || '未归属';
};

const formatNode = (item: BaseRow): string =>
  showNodeNames.value
    ? `${item.node_name} (#${item.node_id})`
    : `节点 #${item.node_id}`;

const downloadPoster = async () => {
  if (!posterRef.value || posterExporting.value) return;
  posterExporting.value = true;
  try {
    await nextTick();
    const { default: html2canvas } = await import('html2canvas');
    const canvas = await html2canvas(posterRef.value, {
      backgroundColor: '#071b2b',
      scale: 2,
      useCORS: true,
      logging: false,
    });
    const stamp = new Date().toISOString().slice(0, 10);
    const filename = `热血江湖排行榜-${stamp}.png`;
    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((value) => {
        if (value) resolve(value);
        else reject(new Error('canvas blob unavailable'));
      }, 'image/png');
    });
    const file = new File([blob], filename, { type: 'image/png' });
    const shareData = { files: [file], title: '热血江湖 · 实时榜单' };
    const canNativeShare = typeof navigator.share === 'function'
      && typeof navigator.canShare === 'function'
      && navigator.canShare(shareData);
    if (canNativeShare) {
      await navigator.share(shareData);
      message.success('海报已分享');
    } else {
      const link = document.createElement('a');
      link.download = filename;
      link.href = canvas.toDataURL('image/png');
      link.click();
      message.success('排行榜海报已保存');
    }
  } catch (error: any) {
    if (error?.name !== 'AbortError') {
      message.error('海报生成失败，请重试');
    }
  } finally {
    posterExporting.value = false;
  }
};

const normalizeNodes = <T extends { node_name?: string }>(rows: T[]) =>
  rows.map((row) => ({
    ...row,
    node_name: row.node_name || '未知节点',
  }));

const loadDuration = async () => {
  loadingDuration.value = true;
  try {
    const r = await getConnectionLeaderboard(limit.value);
    const rows = normalizeNodes(r.data?.items || []) as (ProxyConnectionLeaderboardItem & { node_name: string })[];
    durationRows.value = rows.map((item, idx) => ({
      ...item,
      rank: idx + 1,
    }));
  } catch {
    message.error('加载时长排行失败');
    durationRows.value = [];
  } finally {
    loadingDuration.value = false;
  }
};

const loadTraffic = async () => {
  loadingTraffic.value = true;
  try {
    const r = await getTrafficLeaderboard(limit.value);
    const rows = normalizeNodes(r.data?.items || []) as (ProxyTrafficLeaderboardItem & { node_name: string })[];
    trafficRows.value = rows.map((item, idx) => ({
      ...item,
      rank: idx + 1,
    }));
  } catch {
    message.error('加载流量排行失败');
    trafficRows.value = [];
  } finally {
    loadingTraffic.value = false;
  }
};

const loadSpeed = async () => {
  loadingSpeed.value = true;
  try {
    const r = await getSpeedLeaderboard(limit.value);
    const rows = normalizeNodes(r.data?.items || []) as (ProxySpeedLeaderboardItem & { node_name: string })[];
    speedRows.value = rows.map((item, idx) => ({
      ...item,
      rank: idx + 1,
    }));
  } catch {
    message.error('加载速度排行失败');
    speedRows.value = [];
  } finally {
    loadingSpeed.value = false;
  }
};

const loadAll = async () => {
  await Promise.all([loadDuration(), loadTraffic(), loadSpeed()]);
  lastUpdatedAt.value = new Date();
};

const startAutoRefresh = () => {
  if (refreshTimer) return;
  refreshTimer = window.setInterval(loadAll, 5000);
};

onMounted(() => {
  loadAll().then(() => startAutoRefresh());
});

onUnmounted(() => {
  if (refreshTimer) {
    window.clearInterval(refreshTimer);
    refreshTimer = undefined;
  }
});
</script>

<style scoped>
.leaderboard-page {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.leaderboard-toolbar-inner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.leaderboard-share-controls {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.visibility-control {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #3f4d5f;
  font-size: 12px;
}

.poster-limit-select {
  width: 70px;
}

.helper-text {
  color: #6b7280;
  font-size: 12px;
}

.leaderboard-card {
  border-radius: 10px;
  overflow: hidden;
}

.leaderboard-list-wrap {
  min-height: 420px;
}

.leaderboard-list-item {
  padding: 12px 14px;
  border-top: 1px solid #f0f2f5;
  display: grid;
  grid-template-columns: 64px 1fr 110px;
  gap: 10px;
  align-items: center;
}

.leaderboard-list-item:first-child {
  border-top: none;
}

.leaderboard-item-head {
  display: flex;
  align-items: center;
  gap: 6px;
}

.leaderboard-item-name {
  font-family: ui-monospace, Menlo, Monaco, Consolas, monospace;
  font-size: 13px;
  color: #111827;
}

.leaderboard-item-main {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.meta-line {
  font-size: 12px;
  color: #6b7280;
}

.leaderboard-item-metric {
  justify-self: end;
  text-align: right;
}

.metric-label {
  color: #6b7280;
  font-size: 11px;
}

.metric-value {
  color: #111827;
  font-weight: 600;
  font-size: 15px;
}

.metric-sub {
  margin-top: 6px;
  color: #6b7280;
  font-size: 11px;
}

.leaderboard-empty {
  padding: 36px 16px;
  text-align: center;
  color: #9ca3af;
}

.leaderboard-poster {
  min-width: 0;
}

.poster-hero,
.poster-footer {
  display: none;
}

.leaderboard-poster--active {
  position: relative;
  overflow: hidden;
  padding: 30px;
  border-radius: 22px;
  background:
    radial-gradient(circle at 88% 0%, rgba(49, 215, 197, 0.2), transparent 32%),
    linear-gradient(145deg, #071b2b 0%, #0d2b40 58%, #0a2133 100%);
  box-shadow: 0 24px 60px rgba(7, 27, 43, 0.22);
}

.leaderboard-poster--active::before {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.035) 1px, transparent 1px);
  background-size: 36px 36px;
  content: "";
  pointer-events: none;
}

.leaderboard-poster--active .poster-hero,
.leaderboard-poster--active .leaderboard-grid,
.leaderboard-poster--active .poster-footer {
  position: relative;
  z-index: 1;
}

.leaderboard-poster--active .poster-hero {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 24px;
  color: #fff;
}

.poster-eyebrow {
  color: #55e4d1;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  letter-spacing: 0.22em;
}

.poster-hero h1 {
  margin: 7px 0 0;
  color: #fff;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
  font-size: 30px;
  font-weight: 800;
  letter-spacing: -0.04em;
  line-height: 1.15;
}

.poster-hero p {
  margin: 7px 0 0;
  color: #9fc0d2;
  font-size: 13px;
  letter-spacing: 0.12em;
}

.poster-hero-meta {
  display: flex;
  align-items: flex-end;
  gap: 5px;
  color: #a9c4d3;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 10px;
  flex-direction: column;
}

.leaderboard-poster--active .leaderboard-card {
  height: 100%;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 14px;
  background: rgba(250, 253, 255, 0.97);
  box-shadow: 0 16px 32px rgba(0, 12, 23, 0.2);
}

.leaderboard-poster--active .leaderboard-card--duration {
  border-top: 4px solid #2da4ff;
}

.leaderboard-poster--active .leaderboard-card--traffic {
  border-top: 4px solid #32c99a;
}

.leaderboard-poster--active .leaderboard-card--speed {
  border-top: 4px solid #ff9f43;
}

.leaderboard-poster--active :deep(.ant-card-head) {
  min-height: 54px;
  border-bottom-color: #e8eef4;
}

.leaderboard-poster--active :deep(.ant-card-head-title) {
  color: #102a3c;
  font-size: 14px;
  font-weight: 800;
}

.leaderboard-poster--active .leaderboard-list-wrap {
  min-height: 0;
}

.leaderboard-poster--active .leaderboard-list-item {
  min-height: 90px;
  padding: 14px;
  border-top-color: #e8eef4;
  grid-template-columns: 56px minmax(0, 1fr) 104px;
}

.leaderboard-poster--active .leaderboard-item-head :deep(.ant-tag) {
  display: grid;
  width: 34px;
  height: 34px;
  place-items: center;
  margin: 0;
  border: 0;
  border-radius: 50%;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12px;
  font-weight: 800;
}

.leaderboard-poster--active .leaderboard-item-name {
  color: #7890a0;
  font-size: 11px;
}

.leaderboard-poster--active .meta-line {
  color: #617584;
  line-height: 1.35;
}

.leaderboard-poster--active .meta-line:first-child {
  color: #17384c;
  font-weight: 700;
}

.leaderboard-poster--active .metric-value {
  color: #0c273a;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 17px;
}

.leaderboard-poster--active .poster-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;
  color: #82a7ba;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 10px;
  letter-spacing: 0.06em;
}

@media (max-width: 767px) {
  .leaderboard-toolbar-inner,
  .leaderboard-share-controls {
    align-items: stretch;
    flex-direction: column;
  }

  .leaderboard-share-controls {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;
  }

  .visibility-control {
    min-height: 40px;
    justify-content: space-between;
    padding: 0 10px;
    border: 1px solid #e1e8ef;
    border-radius: 8px;
    background: #f8fafc;
  }

  .leaderboard-share-controls :deep(.ant-btn) {
    width: 100%;
  }

  .leaderboard-list-item {
    grid-template-columns: 54px minmax(0, 1fr);
  }

  .leaderboard-item-metric {
    grid-column: 2;
    justify-self: start;
    text-align: left;
  }

  .leaderboard-poster--active {
    padding: 22px 14px;
    border-radius: 14px;
  }

  .leaderboard-poster--active .poster-hero {
    align-items: flex-start;
    flex-direction: column;
  }

  .poster-hero h1 {
    font-size: 25px;
  }

  .poster-hero-meta {
    align-items: flex-start;
  }

  .leaderboard-poster--active .leaderboard-list-item {
    grid-template-columns: 52px minmax(0, 1fr);
  }

  .leaderboard-poster--active .leaderboard-item-metric {
    grid-column: 2;
  }
}

@media (prefers-reduced-motion: reduce) {
  .leaderboard-poster *,
  .leaderboard-toolbar * {
    transition: none !important;
  }
}
</style>
